import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FiCheck, FiSave, FiTrash2, FiX } from "react-icons/fi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import * as general from "../../libs/general";
import axiosInstance from "../../axiosInstance";
import "../../css/DepartmentModule.css";

const emptyUser = {
  id: null,
  username: "",
  email: "",
  fullName: "",
  divisionId: "",
  divisionIds: [],
  dob: "",
  phone: "",
  address: "",
  note: "",
};

/** Chuẩn hóa không dấu (để lọc tìm kiếm). */
function normalizeSearchText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** Phòng ban khớp → tất cả bộ phận; chỉ bộ phận khớp → nhóm kèm tên phòng ban. */
function buildDivisionPickerRows(departments, queryRaw) {
  const q = normalizeSearchText(queryRaw);
  const rows = [];
  for (const dep of departments || []) {
    const divs = dep.divisions || [];
    const depMatches = !q || normalizeSearchText(dep.name || "").includes(q);
    const visibleDivs = divs.filter((div) => {
      if (!q) return true;
      if (depMatches) return true;
      return normalizeSearchText(div.name || "").includes(q);
    });
    if (!visibleDivs.length) continue;
    rows.push({ kind: "department", department: dep });
    for (const div of visibleDivs) {
      rows.push({ kind: "division", department: dep, division: div });
    }
  }
  return rows;
}

const UserAdminEditor = ({
  show,
  model,
  onClose,
  onSave,
  onResetPassword,
  onAfterDelete,
  existingUsers = [],
  readOnly = false,
}) => {
  const [form, setForm] = useState(emptyUser);
  const [errors, setErrors] = useState({});
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [divisionMenuOpen, setDivisionMenuOpen] = useState(false);
  const [divisionSearch, setDivisionSearch] = useState("");
  const divisionPickerRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    const merged = model ? { ...emptyUser, ...model } : { ...emptyUser };
    const raw = model || {};
    const lastLogin = raw.lastLoginAt ?? raw.last_login_at;
    if (lastLogin != null) merged.lastLoginAt = lastLogin;
    const rawDivIds = Array.isArray(raw.divisionIds)
      ? raw.divisionIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n))
      : [];
    merged.divisionIds =
      rawDivIds.length > 0
        ? rawDivIds
        : raw.divisionId != null && String(raw.divisionId).trim() !== ""
          ? [Number(raw.divisionId)]
          : [];
    merged.divisionId = merged.divisionIds[0] ?? "";
    const editingExisting =
      raw.id != null && String(raw.id).trim() !== "" && Number(raw.id) > 0;
    if (editingExisting) {
      merged.isMember = raw.isMember === true;
      merged.isAdmin = raw.isAdmin === true;
    } else {
      /** Tạo mới từ màn «Tạo mới user» — thành viên nội bộ (khác user lần đầu tạo khi đăng nhập Google). */
      merged.isMember = true;
      merged.isAdmin = false;
    }
    setForm(merged);
    setErrors({});
    setDivisionMenuOpen(false);
    setDivisionSearch("");
  }, [show, model]);

  /** Lấy lại user từ server để có lastLoginAt mới nhất (danh sách có thể thiếu hoặc cũ). */
  useEffect(() => {
    if (!show || model?.id == null || Number(model.id) <= 0) return;
    let cancelled = false;
    axiosInstance
      .get("/api/users/getUser", { params: { id: model.id } })
      .then((res) => {
        if (cancelled) return;
        const dto = res?.data?.data;
        if (!dto) return;
        const ts = dto.lastLoginAt ?? dto.last_login_at;
        if (ts != null) {
          setForm((prev) => ({ ...prev, lastLoginAt: ts }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [show, model?.id]);

  useEffect(() => {
    if (!show) return;
    axiosInstance
      .get("/api/users/departments")
      .then((response) => setDepartments(response.data || []))
      .catch(() => setDepartments([]));
  }, [show]);

  const selectedDivisionPairs = useMemo(() => {
    const ids = Array.isArray(form.divisionIds) ? form.divisionIds : [];
    return ids.map((divId) => {
      for (const d of departments) {
        const div = (d.divisions || []).find((x) => String(x.id) === String(divId));
        if (div) {
          return {
            deptId: d.id,
            deptName: d.name || "",
            divisionId: div.id,
            divName: div.name || "",
          };
        }
      }
      return { deptId: null, deptName: "", divisionId: divId, divName: `ID ${divId}` };
    });
  }, [departments, form.divisionIds]);

  const uniqueDeptLabels = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of selectedDivisionPairs) {
      if (p.deptId == null) continue;
      const key = String(p.deptId);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: p.deptId, name: p.deptName || "" });
    }
    return out;
  }, [selectedDivisionPairs]);

  const divisionSummaryText = useMemo(
    () =>
      selectedDivisionPairs
        .map((p) => p.divName)
        .filter(Boolean)
        .join(", "),
    [selectedDivisionPairs]
  );

  const pickerRows = useMemo(
    () => buildDivisionPickerRows(departments, divisionSearch),
    [departments, divisionSearch]
  );

  useEffect(() => {
    if (!divisionMenuOpen) return;
    const onDocMouseDown = (e) => {
      const el = divisionPickerRef.current;
      if (el && !el.contains(e.target)) {
        setDivisionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [divisionMenuOpen]);

  const handleChange = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleDivision = (div) => {
    if (div?.id == null) return;
    const idNum = Number(div.id);
    setForm((prev) => {
      const cur = Array.isArray(prev.divisionIds) ? [...prev.divisionIds] : [];
      const idx = cur.findIndex((x) => Number(x) === idNum);
      if (idx >= 0) cur.splice(idx, 1);
      else cur.push(idNum);
      return {
        ...prev,
        divisionIds: cur,
        divisionId: cur.length ? cur[0] : "",
      };
    });
    setErrors((prev) => ({ ...prev, divisionIds: "" }));
  };

  const handleDivisionFocus = () => {
    if (readOnly) return;
    setDivisionMenuOpen(true);
    setDivisionSearch("");
  };

  const handleDivisionInputChange = (e) => {
    if (readOnly) return;
    const v = e.target.value;
    setDivisionSearch(v);
    if (!divisionMenuOpen) setDivisionMenuOpen(true);
  };

  const divisionInputValue = divisionMenuOpen ? divisionSearch : divisionSummaryText;

  const handleSubmit = () => {
    if (readOnly) return;
    const nextErrors = {};
    if (!String(form.fullName || "").trim()) {
      nextErrors.fullName = "Phải nhập họ tên";
    }
    const hasUsername = !!String(form.username || "").trim();
    const hasEmail = !!String(form.email || "").trim();
    if (!hasUsername && !hasEmail) {
      nextErrors.username = "Phải nhập tên đăng nhập hoặc email";
      nextErrors.email = "Phải nhập tên đăng nhập hoặc email";
    }
    const duplicateUsername = hasUsername && existingUsers.some(
      (u) => String(u.id) !== String(form.id || "") && String(u.username || "").trim().toLowerCase() === String(form.username).trim().toLowerCase()
    );
    if (duplicateUsername) nextErrors.username = "Tên đăng nhập đã tồn tại";
    const dupByEmail = hasEmail
      ? existingUsers.find(
          (u) =>
            String(u.id) !== String(form.id || "") &&
            String(u.email || "").trim().toLowerCase() === String(form.email).trim().toLowerCase()
        )
      : null;
    if (dupByEmail && dupByEmail.isMember === true) {
      nextErrors.email = "Email đã tồn tại";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    const ids = Array.isArray(form.divisionIds)
      ? form.divisionIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n))
      : [];
    onSave({
      ...form,
      divisionIds: ids,
      divisionId: ids.length ? ids[0] : null,
    });
  };

  const handleConfirmResetPassword = async () => {
    if (readOnly || !form.id) return;
    await axiosInstance.get("/api/users/resetPassword", { params: { id: form.id } });
    setShowResetConfirm(false);
    if (onResetPassword) onResetPassword();
  };

  const hasNoDivision =
    !Array.isArray(form.divisionIds) || form.divisionIds.length === 0;
  const showRemoveInternalMemberBtn =
    !readOnly &&
    !!form.id &&
    form.isMember === true &&
    form.isAdmin !== true &&
    hasNoDivision;

  const handleRemoveInternalMember = async () => {
    if (readOnly || !form.id || !showRemoveInternalMemberBtn) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Loại khỏi thành viên nội bộ",
      html:
        "<p class=\"mb-2\">User sẽ <strong>không còn là thành viên nội bộ</strong> (isMember = false) và không gắn bộ phận nào.</p>" +
        "<p class=\"mb-0 text-muted small\">Thao tác này có thể ảnh hưởng quyền truy cập hệ thống của user.</p>",
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: "Loại bỏ",
      cancelButtonText: "Hủy",
      focusCancel: true,
      customClass: {
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-outline-secondary btn-sm",
        actions: "gap-2",
      },
    });
    if (!confirm.isConfirmed) return;
    onSave({
      ...form,
      isMember: false,
      divisionIds: [],
      divisionId: null,
    });
  };

  const handleDeleteUser = async () => {
    if (readOnly || !form.id) return;
    const label = String(form.fullName || form.username || form.email || `#${form.id}`).trim();
    const confirm = await Swal.fire({
      icon: false,
      title: "Xóa user?",
      text: `Bạn có chắc muốn xóa hẳn user "${label}" khỏi cơ sở dữ liệu? Thao tác không hoàn tác. Nếu user còn đơn hàng, sản phẩm, hóa đơn hoặc dữ liệu nghiệp vụ khác, hệ thống sẽ từ chối xóa.`,
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: "Xóa",
      cancelButtonText: "Hủy",
      focusCancel: true,
      customClass: {
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-outline-secondary btn-sm",
        actions: "gap-2",
      },
    });
    if (!confirm.isConfirmed) return;
    try {
      const { data } = await axiosInstance.put(`/api/users/delete/${form.id}`);
      if (!data?.success) {
        toast.error(data?.message || "Xóa user thất bại.");
        return;
      }
      toast.success(data?.message || "Đã xóa user khỏi cơ sở dữ liệu.");
      if (typeof onAfterDelete === "function") onAfterDelete();
      onClose();
    } catch (err) {
      const raw = err?.response?.data;
      let msg = err?.message || "Xóa user thất bại.";
      if (typeof raw === "string") msg = raw;
      else if (raw && typeof raw.message === "string") msg = raw.message;
      toast.error(msg);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" dialogClassName="department-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {readOnly && form.id ? "Chi tiết user" : form.id ? "Cập nhật user" : "Tạo mới user"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Họ tên</Form.Label>
              <Form.Control
                value={form.fullName}
                readOnly={readOnly}
                isInvalid={!!errors.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Tên đăng nhập</Form.Label>
              <Form.Control
                value={form.username}
                readOnly={readOnly}
                isInvalid={!!errors.username}
                onChange={(e) => handleChange("username", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={form.email}
                readOnly={readOnly}
                isInvalid={!!errors.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Ngày sinh</Form.Label>
              <Form.Control
                type="date"
                value={form.dob}
                readOnly={readOnly}
                onChange={(e) => handleChange("dob", e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Số điện thoại</Form.Label>
              <Form.Control
                value={form.phone}
                readOnly={readOnly}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Địa chỉ</Form.Label>
              <Form.Control
                value={form.address}
                readOnly={readOnly}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Phòng ban</Form.Label>
              <div className="form-control bg-light user-admin-dept-display">
                {uniqueDeptLabels.length ? (
                  uniqueDeptLabels.map((d) => (
                    <span key={d.id} className="badge rounded-pill bg-secondary-subtle text-dark border">
                      {d.name}
                    </span>
                  ))
                ) : (
                  <span className="text-muted user-admin-dept-display__empty">—</span>
                )}
              </div>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Bộ phận</Form.Label>
              <div className="user-division-picker" ref={divisionPickerRef}>
                <Form.Control
                  readOnly={readOnly}
                  autoComplete="off"
                  aria-expanded={divisionMenuOpen}
                  aria-haspopup="listbox"
                  placeholder={
                    Array.isArray(form.divisionIds) && form.divisionIds.length
                      ? `${form.divisionIds.length} bộ phận đã chọn, gõ để thêm/bỏ`
                      : "Chọn hoặc gõ để tìm bộ phận"
                  }
                  value={divisionInputValue}
                  onChange={handleDivisionInputChange}
                  onFocus={handleDivisionFocus}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setDivisionMenuOpen(false);
                  }}
                />
                {divisionMenuOpen && !readOnly && (
                  <div className="user-division-picker__menu" role="listbox">
                    {!pickerRows.length && (
                      <div className="text-muted small px-3 py-2">Không có bộ phận phù hợp.</div>
                    )}
                    {pickerRows.map((row) => {
                      if (row.kind === "department") {
                        return (
                          <div
                            key={`dep-${row.department.id}`}
                            className="user-division-picker__dep"
                            aria-hidden
                          >
                            {row.department.name || ""}
                          </div>
                        );
                      }
                      const isSelected = (Array.isArray(form.divisionIds) ? form.divisionIds : []).some(
                        (id) => Number(id) === Number(row.division.id)
                      );
                      return (
                        <div
                          key={`div-${row.department.id}-${row.division.id}`}
                          role="option"
                          aria-selected={isSelected}
                          className={`user-division-picker__div${isSelected ? " user-division-picker__div--selected" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => toggleDivision(row.division)}
                        >
                          <span>{row.division.name || ""}</span>
                          <span className={`user-division-picker__check ${isSelected ? "visible" : "invisible"}`}>
                            <FiCheck size={16} strokeWidth={2.5} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group>
              <Form.Label>Ghi chú</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.note}
                readOnly={readOnly}
                onChange={(e) => handleChange("note", e.target.value)}
              />
            </Form.Group>
            <small className="text-muted d-block mt-1">
              {form.lastLoginAt
                ? `Đăng nhập lần cuối: ${general.formatDateTime3(form.lastLoginAt)}`
                : "Chưa có lần đăng nhập ghi nhận"}
            </small>
            {form.passwordSetupRequired === true && (
              <span className="badge bg-warning text-dark mt-2">
                Cần thiết lập mật khẩu
              </span>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap align-items-center gap-2">
        <div className="me-auto d-flex flex-wrap gap-2 align-items-center">
          {!readOnly && !!form.id && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setShowResetConfirm(true)}
            >
              Reset password
            </button>
          )}
          {showRemoveInternalMemberBtn && (
            <Button
              type="button"
              size="sm"
              variant="outline-danger"
              className="d-inline-flex align-items-center gap-1"
              onClick={handleRemoveInternalMember}
            >
              Loại khỏi thành viên nội bộ
            </Button>
          )}
          {!readOnly && !!form.id && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm border-0 p-1 lh-1 d-inline-flex align-items-center"
              title="Xóa user"
              aria-label="Xóa user"
              onClick={handleDeleteUser}
            >
              <FiTrash2 size={20} strokeWidth={2} />
            </button>
          )}
        </div>
        {readOnly ? (
          <Button size="sm" variant="secondary" className="d-inline-flex align-items-center gap-1" onClick={onClose}>
            <FiX size={14} /> Đóng
          </Button>
        ) : (
          <>
            {!form.id && (
              <Button size="sm" variant="secondary" className="d-inline-flex align-items-center gap-1" onClick={onClose}>
                <FiX size={14} /> Hủy
              </Button>
            )}
            <Button size="sm" className="d-inline-flex align-items-center gap-1" onClick={handleSubmit}>
              <FiSave size={14} /> Lưu
            </Button>
          </>
        )}
      </Modal.Footer>

      <Modal
        show={showResetConfirm}
        onHide={() => setShowResetConfirm(false)}
        centered
        dialogClassName="department-modal"
        className="reset-password-confirm-modal"
        backdropClassName="reset-password-confirm-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận reset password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Sau khi reset password, user cần thực hiện thiết lập mật khẩu để đăng nhập.
          </p>
          <p className="mb-0">
            User vẫn có thể đăng nhập bằng Google sign in. Nếu không dùng Google sign in, user cần thiết lập mật khẩu trước khi đăng nhập.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setShowResetConfirm(false)}>
            Hủy
          </Button>
          <Button size="sm" variant="warning" onClick={handleConfirmResetPassword}>
            Xác nhận reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default UserAdminEditor;
